/**
 * Wave 3.99i / 3.99j — Change Customer on an order.
 *
 * Simpler than ChangeKarigar (which cancels + re-creates the order) —
 * this only re-links the ``customer_id`` on the same order. It is
 * gated in the UI to Super Admin / Company Admin and only appears when
 * the order stage is NOT ``Completed``. The backend re-enforces both
 * rules on ``PUT /api/orders/{id}``.
 *
 * Wave 3.99j fix: switched to the ``SearchableSelect`` API used by the
 * rest of the portal (items + itemToValue + itemToLabel + onValueChange)
 * so the dropdown actually renders customer rows. Also scopes customer
 * lookups to ``order.company_id`` (rather than the auth-derived active
 * company) so a Super Admin who opened the order via a direct URL
 * still sees the right list, and filters out the currently-linked
 * customer so operators only see the choices they can switch TO.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { extractError } from '@/lib/errors';
import { Loader2, Users, EraserIcon } from 'lucide-react';

// Sentinel value so we can differentiate "clear the link" from an empty
// selection. Kept internal to this component.
const CLEAR_VALUE = '__clear_customer__';

export function ChangeCustomerDialog({
  open,
  onOpenChange,
  order,
  companyId,          // legacy prop — retained for compatibility
  onSaved,            // (updatedOrder) => void
}) {
  const [customers, setCustomers] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Prefer the order's own company_id — always present and correct even
  // when the caller couldn't pass a company scope (e.g. Super Admin
  // opened the order via a deep URL before picking a company).
  const scopedCompanyId = order?.company_id || companyId;

  // Reset state on open.
  useEffect(() => {
    if (open) {
      setSelectedId('');
      setSubmitting(false);
    }
  }, [open]);

  // Load customers scoped to the order's company.
  useEffect(() => {
    if (!open || !scopedCompanyId) return;
    setLoading(true);
    api
      .get('/masters/customers', { params: { company_id: scopedCompanyId } })
      .then(({ data }) => setCustomers(Array.isArray(data) ? data : []))
      .catch((e) => toast.error(extractError(e, 'Failed to load customers')))
      .finally(() => setLoading(false));
  }, [open, scopedCompanyId]);

  // Everything except the currently-linked customer + archived rows.
  const availableCustomers = useMemo(() => {
    const currentId = order?.customer_id || '';
    return customers.filter(
      (c) => !c.is_archived && c.id !== currentId,
    );
  }, [customers, order?.customer_id]);

  // Build the option pool. If the order currently HAS a customer, prepend
  // a "clear" row so operators can un-link without picking another one.
  const options = useMemo(() => {
    const list = [...availableCustomers];
    if (order?.customer_id) {
      list.unshift({
        id: CLEAR_VALUE,
        name: 'Clear customer link',
        phone: '',
        __clear: true,
      });
    }
    return list;
  }, [availableCustomers, order?.customer_id]);

  const submit = async () => {
    if (submitting) return;
    if (!selectedId) {
      toast.error('Please pick a customer to switch to.');
      return;
    }
    const currentId = order?.customer_id || '';
    const desiredId = selectedId === CLEAR_VALUE ? '' : selectedId;
    if (desiredId === currentId) {
      onOpenChange(false);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.put(`/orders/${order.id}`, {
        customer_id: desiredId || '',
      });
      toast.success(
        desiredId ? 'Customer updated on this order' : 'Customer cleared from this order',
      );
      onOpenChange(false);
      if (onSaved) onSaved(data);
    } catch (e) {
      toast.error(extractError(e, 'Failed to update customer'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!submitting) onOpenChange(v); }}>
      <DialogContent className="max-w-lg" data-testid="change-customer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users size={18} className="text-[var(--ink-navy)]" /> Change Customer
          </DialogTitle>
          <DialogDescription>
            Re-link order&nbsp;
            <span className="font-mono font-medium text-[var(--ink-navy)]">
              {order?.order_no}
            </span>
            &nbsp;to a different customer. Currently linked to{' '}
            <span className="font-medium text-[var(--ink-navy)]">
              {order?.customer_name || '(none)'}
            </span>
            .
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">
              New customer
            </Label>
            <SearchableSelect
              items={options}
              value={selectedId}
              onValueChange={(v) => setSelectedId(v)}
              itemToValue={(c) => c.id}
              itemToLabel={(c) => {
                if (c.__clear) return '— Clear customer link —';
                return c.name + (c.phone ? ` · ${c.phone}` : '');
              }}
              searchKeys={['name', 'phone', 'email']}
              placeholder={loading ? 'Loading customers…' : 'Search customers by name or phone…'}
              disabled={loading || submitting}
              emptyLabel={
                loading
                  ? 'Loading…'
                  : (customers.length === 0
                      ? 'No customers found in this company. Add one from the Customers master first.'
                      : (availableCustomers.length === 0
                          ? 'No other customers available to switch to.'
                          : 'No matches for your search.'))
              }
              testId="change-customer-picker"
            />
            <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-1">
              Only active customers are listed. The currently linked customer is
              hidden so you cannot pick it again.
              {loading && <Loader2 size={11} className="animate-spin ml-1" />}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="change-customer-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || !selectedId}
            className="luxury-button"
            data-testid="change-customer-submit"
          >
            {submitting ? (
              <><Loader2 size={14} className="mr-1.5 animate-spin" /> Saving…</>
            ) : selectedId === CLEAR_VALUE ? (
              <><EraserIcon size={14} className="mr-1.5" /> Clear link</>
            ) : (
              'Save'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ChangeCustomerDialog;
